import React, { useMemo, useState } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import {
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledNumber,
  MissingFields,
  QuestionNumber,
  noop,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'

// `RecalculateScores.tsx`'s formula: only `WPAI_SCORE_4` is always computed, `q3` is stored but never scored.
const calculateScores = (q1: boolean, q2: number, q4: number, q5: number | undefined, q6: number) => {
  const score4 = Math.round((q6 / 10) * 100)

  if (!q1) {
    return { WPAI_SCORE_1: null, WPAI_SCORE_2: null, WPAI_SCORE_3: null, WPAI_SCORE_4: score4 }
  }

  const score1 = q2 / (q2 + q4)
  const score2 = q5 === undefined ? NaN : q5 / 10
  const score3 = Number.isNaN(score2) && q4 === 0 ? score1 : score1 + (1 - score1) * score2

  return {
    WPAI_SCORE_1: Number.isFinite(score1) ? Math.round(score1 * 100) : null,
    WPAI_SCORE_2: q4 > 0 && q5 !== undefined ? Math.round(score2 * 100) : null,
    WPAI_SCORE_3: Number.isFinite(score3) ? Math.round(score3 * 100) : null,
    WPAI_SCORE_4: score4,
  }
}

const QUESTION_MISSED_ILLNESS =
  'During the past seven days, how many hours did you miss from work because of problems associated with your depression or burnout?'
const QUESTION_MISSED_OTHER =
  'During the past seven days, how many hours did you miss from work because of any other reason, such as vacation, holidays, time off to participate in this study?'
const QUESTION_WORKED = 'During the past seven days, how many hours did you actually work?'
const QUESTION_PRODUCTIVITY =
  'During the past seven days, how much did your depression or burnout affect your productivity while you were working?'
const QUESTION_DAILY_ACTIVITIES =
  'During the past seven days, how much did your depression or burnout affect your ability to do your regular daily activities, other than work at a job?'

const scoreOrBlank = (score: number | null): number | '' => (score === null || Number.isNaN(score) ? '' : score)

const WPAIFields = ({ uid }: { uid: string }) => {
  const [date, setDate] = useState(todayIso())
  const [employed, setEmployed] = useState(false)
  const [hoursMissedIllness, setHoursMissedIllness] = useState<number | ''>('')
  const [hoursMissedOther, setHoursMissedOther] = useState<number | ''>('')
  const [hoursWorked, setHoursWorked] = useState<number | ''>('')
  const [productivityWhileWorking, setProductivityWhileWorking] = useState<number | ''>('')
  const [dailyActivityImpact, setDailyActivityImpact] = useState<number | ''>('')
  const { submit, submitting, error } = useSubmitPatientHistory(uid)

  const hoursMissedIllnessNumber = hoursMissedIllness === '' ? 0 : hoursMissedIllness
  const hoursWorkedNumber = hoursWorked === '' ? 0 : hoursWorked
  const dailyActivityImpactNumber = dailyActivityImpact === '' ? 0 : dailyActivityImpact
  const productivityNumber = productivityWhileWorking === '' ? undefined : productivityWhileWorking

  const scores = useMemo(
    () =>
      calculateScores(
        employed,
        hoursMissedIllnessNumber,
        hoursWorkedNumber,
        productivityNumber,
        dailyActivityImpactNumber
      ),
    [employed, hoursMissedIllnessNumber, hoursWorkedNumber, productivityNumber, dailyActivityImpactNumber]
  )

  // As in Standard's schema: the hours need an answer when employed, productivity once any hours were worked.
  const productivityNeeded = employed && hoursWorkedNumber > 0
  const required = useRequiredFields({
    'Date of event': Boolean(date),
    ...(employed && {
      [QUESTION_MISSED_ILLNESS]: hoursMissedIllness !== '',
      [QUESTION_MISSED_OTHER]: hoursMissedOther !== '',
      [QUESTION_WORKED]: hoursWorked !== '',
    }),
    ...(productivityNeeded && { [QUESTION_PRODUCTIVITY]: productivityWhileWorking !== '' }),
    [QUESTION_DAILY_ACTIVITIES]: dailyActivityImpact !== '',
  })

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    const value: Record<string, unknown> = {
      WPAI_QUESTION_1: employed,
      WPAI_QUESTION_6: dailyActivityImpactNumber,
      ...Object.fromEntries(Object.entries(scores).filter(([, score]) => score !== null)),
    }

    if (employed) {
      value.WPAI_QUESTION_2 = hoursMissedIllnessNumber
      value.WPAI_QUESTION_3 = hoursMissedOther === '' ? 0 : hoursMissedOther
      value.WPAI_QUESTION_4 = hoursWorkedNumber

      if (hoursWorkedNumber > 0 && productivityNumber !== undefined) {
        value.WPAI_QUESTION_5 = productivityNumber
      }
    }

    await submit({
      date,
      type: PatientHistoryType.Wpai,
      value,
    })
    setEmployed(false)
    setHoursMissedIllness('')
    setHoursMissedOther('')
    setHoursWorked('')
    setProductivityWhileWorking('')
    setDailyActivityImpact('')
    required.reset()
  }

  return (
    <FieldSection title="WPAI">
      <FieldGrid>
        <LabeledDate
          label="Date of event"
          value={date}
          onChange={setDate}
          required
          error={required.error('Date of event')}
        />
      </FieldGrid>
      <LabeledCheckbox
        label="Are you currently employed (working for pay)?"
        checked={employed}
        onChange={setEmployed}
      />

      {employed && (
        <>
          <QuestionNumber
            question={QUESTION_MISSED_ILLNESS}
            value={hoursMissedIllness}
            onChange={setHoursMissedIllness}
            min={0}
            required
            error={required.error(QUESTION_MISSED_ILLNESS)}
          />
          <QuestionNumber
            question={QUESTION_MISSED_OTHER}
            value={hoursMissedOther}
            onChange={setHoursMissedOther}
            min={0}
            required
            error={required.error(QUESTION_MISSED_OTHER)}
          />
          <QuestionNumber
            question={QUESTION_WORKED}
            value={hoursWorked}
            onChange={setHoursWorked}
            min={0}
            required
            error={required.error(QUESTION_WORKED)}
          />
          {hoursWorkedNumber > 0 && (
            <QuestionNumber
              question={QUESTION_PRODUCTIVITY}
              placeholder="0-10"
              min={0}
              max={10}
              value={productivityWhileWorking}
              onChange={setProductivityWhileWorking}
              required
              error={required.error(QUESTION_PRODUCTIVITY)}
            />
          )}
        </>
      )}

      <QuestionNumber
        question={QUESTION_DAILY_ACTIVITIES}
        placeholder="0-10"
        min={0}
        max={10}
        value={dailyActivityImpact}
        onChange={setDailyActivityImpact}
        required
        error={required.error(QUESTION_DAILY_ACTIVITIES)}
      />

      <FieldSection title="Calculated scores">
        <FieldGrid>
          <LabeledNumber
            label="Percent work time missed due to health"
            value={scoreOrBlank(scores.WPAI_SCORE_1)}
            disabled
            onChange={noop}
          />
          <LabeledNumber
            label="Percent impairment while working due to health"
            value={scoreOrBlank(scores.WPAI_SCORE_2)}
            disabled
            onChange={noop}
          />
          <LabeledNumber
            label="Percent overall work impairment due to health"
            value={scoreOrBlank(scores.WPAI_SCORE_3)}
            disabled
            onChange={noop}
          />
          <LabeledNumber
            label="Percent activity impairment due to health"
            value={dailyActivityImpact === '' ? '' : scoreOrBlank(scores.WPAI_SCORE_4)}
            disabled
            onChange={noop}
          />
        </FieldGrid>
      </FieldSection>

      <MissingFields labels={required.missing} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

export default WPAIFields
